import { DetailedRound } from "../../../models/round";

export const THE_GAME_KEY = "THE_GAME";
export const THE_GAME_STANDINGS_KEY = "THE_GAME_STANDINGS";

// For lists that only carry the round name (no promotional values); round types are named "TheGame-<n>".
export function isTheGameRoundName(name?: string): boolean {
  return !!name && name.startsWith("TheGame");
}

export type ManifestSign = "1" | "X" | "2";

export interface TheGameMarket {
  pos: number;
  marketTypeId?: number;
  label: string;
  line?: number;
  selections: Partial<Record<ManifestSign, string>>;
  // Legacy: standings now come in THE_GAME_STANDINGS, these are only a fallback.
  note?: string;
  decided?: boolean;
}

export interface TheGameManifest {
  match: { fixtureId?: number; home: string; away: string; kickoff?: string };
  markets: TheGameMarket[];
}

export interface TheGameStandingsMarket {
  pos: number;
  sign: ManifestSign;
  decided: boolean;
  note?: string;
}

export interface TheGameStandings {
  updatedAt?: string;
  markets: TheGameStandingsMarket[];
}

const manifestSignOrder: ManifestSign[] = ["1", "X", "2"];

// Coupon legs use the ONE/X/TWO enum, the manifest uses 1/X/2.
const couponSignByManifestSign: Record<ManifestSign, string> = { "1": "ONE", X: "X", "2": "TWO" };

export function toCouponSign(sign: ManifestSign): string {
  return couponSignByManifestSign[sign];
}

export function toManifestSign(couponSign: string): ManifestSign | undefined {
  return manifestSignOrder.find((sign) => couponSignByManifestSign[sign] === couponSign);
}

export function marketSigns(market: TheGameMarket): ManifestSign[] {
  return manifestSignOrder.filter((sign) => market.selections[sign] !== undefined);
}

export function getTheGameMatchName(manifest: TheGameManifest): string {
  return `${manifest.match.home} – ${manifest.match.away}`;
}

export function getTheGameManifest(round?: Pick<DetailedRound, "promotionalValues">): TheGameManifest | undefined {
  const entry = round?.promotionalValues?.find((pv) => pv.key === THE_GAME_KEY);
  if (!entry) {
    return undefined;
  }

  try {
    const raw: unknown = entry.value;
    const manifest = (typeof raw === "string" ? JSON.parse(raw) : raw) as TheGameManifest;
    if (!manifest || !Array.isArray(manifest.markets) || !manifest.match) {
      return undefined;
    }
    return { ...manifest, markets: [...manifest.markets].sort((a, b) => a.pos - b.pos) };
  } catch {
    return undefined;
  }
}

export function getTheGameStandings(round?: Pick<DetailedRound, "promotionalValues">): TheGameStandings | undefined {
  const entry = round?.promotionalValues?.find((pv) => pv.key === THE_GAME_STANDINGS_KEY);
  if (!entry) {
    return undefined;
  }

  try {
    const raw: unknown = entry.value;
    const standings = (typeof raw === "string" ? JSON.parse(raw) : raw) as TheGameStandings;
    if (!standings || !Array.isArray(standings.markets)) {
      return undefined;
    }
    return standings;
  } catch {
    return undefined;
  }
}
